import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, FileText, AlertCircle } from 'lucide-react';
import { getDownloadUrl } from './participantDocumentsApi';
import api from '@/lib/api';
import { toast } from 'sonner';

const PREVIEWABLE_MIMES = new Set([
  'application/pdf',
  'image/jpeg', 'image/png', 'image/webp', 'image/gif',
  'text/plain', 'text/csv', 'text/html',
]);

function isPreviewable(mimeType) {
  return mimeType && PREVIEWABLE_MIMES.has(mimeType);
}

export function ParticipantDocumentPreviewModal({ doc, participantId, canDownload, open, onClose }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (!open || !doc) return;
    setLoading(true);
    setError(null);
    setPreviewUrl(null);
    setTextContent(null);

    const url = `/participants/${participantId}/documents/${doc.id}/preview`;

    if (!isPreviewable(doc.mimeType)) {
      setLoading(false);
      setError('This file type cannot be previewed.');
      return;
    }

    if (doc.mimeType === 'text/plain' || doc.mimeType === 'text/csv') {
      api.get(url, { responseType: 'text' })
        .then((res) => {
          setTextContent(typeof res.data === 'string' ? res.data : JSON.stringify(res.data));
          setPreviewType('text');
        })
        .catch(() => setError('Failed to load preview'))
        .finally(() => setLoading(false));
    } else {
      api.get(url, { responseType: 'blob' })
        .then((res) => {
          const blob = new Blob([res.data], { type: doc.mimeType });
          const blobUrl = URL.createObjectURL(blob);
          setPreviewUrl(blobUrl);
          setPreviewType(doc.mimeType.startsWith('image/') ? 'image' : 'pdf');
        })
        .catch(() => setError('Failed to load preview'))
        .finally(() => setLoading(false));
    }

    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [open, doc, participantId]);

  const handleDownload = async () => {
    try {
      const url = getDownloadUrl(participantId, doc.id);
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.fileName || doc.title || 'document';
      link.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      toast.error('Failed to download document');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="preview-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-sm pr-8">
            <FileText className="w-4 h-4 text-muted-foreground" />
            <span className="truncate">{doc?.title}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16" data-testid="preview-loading">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
              <span className="ml-2 text-sm text-muted-foreground">Loading preview...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="preview-unsupported">
              <AlertCircle className="w-8 h-8 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">{error}</p>
              {canDownload && (
                <Button size="sm" variant="outline" onClick={handleDownload} className="mt-4">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download Instead
                </Button>
              )}
            </div>
          ) : previewType === 'image' ? (
            <div className="flex items-center justify-center p-4" data-testid="preview-image">
              <img src={previewUrl} alt={doc?.title} className="max-w-full max-h-[60vh] object-contain rounded" />
            </div>
          ) : previewType === 'pdf' ? (
            <div className="h-[65vh]" data-testid="preview-pdf">
              <iframe src={previewUrl} className="w-full h-full rounded border" title={doc?.title} />
            </div>
          ) : previewType === 'text' ? (
            <pre className="p-4 bg-muted/30 rounded text-xs font-mono whitespace-pre-wrap max-h-[60vh] overflow-auto" data-testid="preview-text">
              {textContent}
            </pre>
          ) : null}
        </div>

        {canDownload && !loading && !error && (
          <div className="flex justify-end pt-2 flex-shrink-0 border-t">
            <Button size="sm" variant="outline" onClick={handleDownload} data-testid="preview-download-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
